import * as React from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import colorSchemes from "../../../constants/colorSchemes";
import useStyles from "./SignIn.styles";
import Form from "react-bootstrap/Form";
import { useState, useContext } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import loginApi from "../../../api/loginAPI";
import { setToken, setUserData } from "../../../common";
import config from "../../../config/config";
import routes from "../../../constants/routes";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { TimerContext } from "../../../app/App";

export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";

// Page to return to after login (set by the auction page login dialog).
// Only auction pages are allowed, so it can't be used to redirect elsewhere.
export const takePostLoginRedirect = () => {
  try {
    const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return path && /^\/auctions\/[\w-]+$/.test(path) ? path : null;
  } catch (error) {
    return null;
  }
};

// Email/password + social login form, shared by the Sign In page and the
// auction page login dialog. onSuccess runs after the session is stored.
// redirectAfterSocialLogin: hash path to come back to after Google/Facebook
// or after registering and signing in.
// registerPath: where "Create free account" leads (the account type choice
// by default; the auction dialog sends bidders straight to their form).
export default function LoginForm({
  onSuccess,
  redirectAfterSocialLogin,
  registerPath = routes.register,
}) {
  const timerContext = useContext(TimerContext);
  const classes = useStyles();

  const [loginInfo, setLoginInfo] = useState({
    email: "",
    password: "",
  });
  const [passwordType, setPasswordType] = useState("password");

  const handleChange = (event) => {
    setLoginInfo({ ...loginInfo, [event.target.name]: event.target.value });
  };

  const togglePassword = () => {
    if (passwordType === "password") {
      setPasswordType("text");
      return;
    }
    setPasswordType("password");
  };

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();

      const { data } = await loginApi(loginInfo);
      setToken(data.token); /* set user token */
      setUserData(data);
      timerContext.timerProps.activate();
      toast("Login Successfully!");
      if (onSuccess) onSuccess(data);
    } catch (error) {
      if (error.response) {
        toast(error.response.data.error);
      } else if (error.request) {
        toast("Something went wrong!");
      } else {
        console.log("Error", error.message);
      }
      console.log(error);
    }
  };

  const rememberRedirect = () => {
    if (!redirectAfterSocialLogin) return;
    try {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirectAfterSocialLogin);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <form layout="vertical" onSubmit={handleSubmit}>
        <Box className={classes.forms}>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label className={classes.label}>E-mail</Form.Label>
            <Form.Control
              className={classes.input}
              type="email"
              placeholder="Enter email"
              name="email"
              value={loginInfo.email}
              onChange={handleChange}
            />
          </Form.Group>
        </Box>
        <Box className={classes.forms}>
          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label className={classes.label}>Password</Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                className={classes.input}
                type={passwordType}
                name="password"
                placeholder="Enter Password"
                value={loginInfo.password}
                onChange={handleChange}
                style={{ paddingRight: "45px" }}
              />
              <button
                type="button"
                onClick={togglePassword}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: "5px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {passwordType === "password" ? (
                  <RemoveRedEyeIcon style={{ color: "#2D55EB" }} />
                ) : (
                  <VisibilityOffIcon style={{ color: "#2D55EB" }} />
                )}
              </button>
            </div>
          </Form.Group>
        </Box>

        <Typography
          className={classes.forgot}
          sx={{ color: colorSchemes.primaryColor }}
        >
          <Link to="/forgot">Forgot Password ?</Link>
        </Typography>
        <Box>
          <Button
            sx={{
              backgroundColor: colorSchemes.primaryColor,
              color: "#fff",
              borderRadius: "0em",
              "&.MuiButtonBase-root:hover": {
                bgcolor: "#2D55EB !important",
              },
            }}
            fullWidth
            type="submit"
          >
            Login
          </Button>
          <a
            href={config.socialLogin.google}
            onClick={rememberRedirect}
            className="px-4 py-2 border flex gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400  transition duration-150 google-login"
          >
            <img
              className="w-6 h-6"
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              loading="lazy"
              alt="google logo"
            />
            <span>Continue with Google</span>
          </a>
          <a
            href={config.socialLogin.facebook}
            onClick={rememberRedirect}
            className="px-4 py-2 border flex gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400  transition duration-150 google-login"
          >
            <img
              className="w-6 h-6"
              src="https://www.svgrepo.com/show/448224/facebook.svg"
              loading="lazy"
              alt="facebook logo"
            />
            <span>Continue with Facebook</span>
          </a>
        </Box>
        <Box></Box>
      </form>
      <Typography className={classes.forgot}>
        Don't have an account?{" "}
        <Link to={registerPath} onClick={rememberRedirect}>
          Create free account
        </Link>{" "}
      </Typography>
    </>
  );
}
